import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getPlatformUsers,
  invitePlatformUser,
  updatePlatformUser,
  deletePlatformUser,
  resendUserInvite,
  getMe,
  type PlatformUser,
  type PlatformRole,
} from '@/services'
import { AppButton, EmptyState, StatusBadge, Avatar, Modal, Drawer, IconButton, Skeleton, TableSkeleton, rowActivation } from '@/components/platform/primitives'
import { relTime } from '@/lib/utils'

const ROLES: PlatformRole[] = ['owner', 'admin', 'support', 'finance', 'viewer']

function userName(u: PlatformUser): string {
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email
}
function initials(u: PlatformUser): string {
  return userName(u).split(' ').map((p) => p[0]).filter(Boolean).join('').slice(0, 2).toUpperCase() || 'BI'
}

export function UsersPage() {
  const qc = useQueryClient()
  const [inviting, setInviting] = useState(false)
  const [editing, setEditing] = useState<PlatformUser | null>(null)

  const { data: result, isLoading } = useQuery({ queryKey: ['platform-users'], queryFn: () => getPlatformUsers({ pageSize: 100 }) })
  const users = result?.data || []
  const invalidate = () => qc.invalidateQueries({ queryKey: ['platform-users'] })

  const inviteMutation = useMutation({
    mutationFn: (body: { email: string; role: PlatformRole; first_name?: string; last_name?: string; whatsapp_phone?: string | null }) => invitePlatformUser(body),
    onSuccess: () => { toast.success('Invitación enviada'); invalidate(); setInviting(false) },
    onError: (e: any) => toast.error(e?.message || 'No se pudo invitar'),
  })
  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<{ role: PlatformRole; is_active: boolean; first_name: string | null; last_name: string | null; whatsapp_phone: string | null }> }) => updatePlatformUser(id, body),
    onSuccess: () => { toast.success('Usuario actualizado'); invalidate(); setEditing(null) },
    onError: (e: any) => toast.error(e?.message || 'No se pudo actualizar'),
  })
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePlatformUser(id),
    onSuccess: () => { toast.success('Usuario eliminado'); invalidate(); setEditing(null) },
    onError: (e: any) => toast.error(e?.message || 'No se pudo eliminar'),
  })
  const resendMutation = useMutation({
    mutationFn: (id: string) => resendUserInvite(id),
    onSuccess: () => toast.success('Invitación reenviada'),
    onError: (e: any) => toast.error(e?.message || 'No se pudo reenviar'),
  })

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Equipo</div>
          <h1>Usuarios del panel</h1>
          <div className="subtitle">{isLoading ? <Skeleton width={260} height={14} /> : `${users.length} usuarios con acceso al panel de plataforma.`}</div>
        </div>
        <div className="page-actions">
          <AppButton variant="primary" icon="plus" onClick={() => setInviting(true)}>Invitar usuario</AppButton>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <TableSkeleton columns={5} rows={7} />
        ) : users.length === 0 ? (
          <EmptyState icon="users" title="Sin usuarios" desc="Invitá al primer miembro del equipo." />
        ) : (
          <table className="table">
            <thead>
              <tr><th>Usuario</th><th>Rol</th><th>Estado</th><th>Último acceso</th><th style={{ width: 90 }} /></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} onClick={() => setEditing(u)} {...rowActivation(() => setEditing(u))}>
                  <td>
                    <div className="row gap-2">
                      <Avatar initials={initials(u)} size="sm" />
                      <div>
                        <div className="cell-primary">{userName(u)}</div>
                        <div className="cell-secondary">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className="badge badge-outline badge-sm">{u.role}</span></td>
                  <td><StatusBadge status={u.is_active ? 'active' : 'cancelled'} size="sm" /></td>
                  <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{u.last_login_at ? relTime(u.last_login_at) : 'nunca'}</span></td>
                  <td>
                    <div className="row gap-2" onClick={(e) => e.stopPropagation()}>
                      {!u.last_login_at ? <IconButton icon="send" tooltip="Reenviar invitación" size={13} onClick={() => resendMutation.mutate(u.id)} /> : null}
                      <IconButton icon="edit" tooltip="Editar" size={13} onClick={() => setEditing(u)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Invite modal */}
      <Modal
        open={inviting}
        onClose={() => setInviting(false)}
        title="Invitar usuario"
        subtitle="Le mandamos un mail para que cree su contraseña."
        footer={
          <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
            <AppButton variant="ghost" onClick={() => setInviting(false)}>Cancelar</AppButton>
            <AppButton variant="primary" icon="send" disabled={inviteMutation.isPending} onClick={() => { const f = document.getElementById('invite-form') as HTMLFormElement | null; f?.requestSubmit() }}>Invitar</AppButton>
          </div>
        }
      >
        {inviting ? (
          <form id="invite-form" className="col gap-4" onSubmit={(e) => {
            e.preventDefault()
            const fd = new FormData(e.currentTarget)
            inviteMutation.mutate({
              email: String(fd.get('email') || '').trim(),
              role: (fd.get('role') as PlatformRole) || 'support',
              first_name: String(fd.get('first_name') || '') || undefined,
              last_name: String(fd.get('last_name') || '') || undefined,
              whatsapp_phone: String(fd.get('whatsapp_phone') || '') || undefined,
            })
          }}>
            <div className="form-row"><label htmlFor="invite-email">Email</label><input id="invite-email" className="input" name="email" type="email" autoComplete="email" required placeholder="persona@bisne.app" /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-row"><label htmlFor="invite-first">Nombre</label><input id="invite-first" className="input" name="first_name" autoComplete="given-name" /></div>
              <div className="form-row"><label htmlFor="invite-last">Apellido</label><input id="invite-last" className="input" name="last_name" autoComplete="family-name" /></div>
            </div>
            <div className="form-row"><label htmlFor="invite-whatsapp">WhatsApp para aprobaciones</label><input id="invite-whatsapp" className="input" name="whatsapp_phone" inputMode="tel" placeholder="549351..." /></div>
            <div className="form-row">
              <label htmlFor="invite-role">Rol</label>
              <select id="invite-role" className="select" name="role" defaultValue="support">{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select>
            </div>
          </form>
        ) : null}
      </Modal>

      {/* Edit drawer */}
      <Drawer open={!!editing} onClose={() => setEditing(null)} title={editing ? userName(editing) : ''}>
        {editing ? (
          <div className="col gap-4">
            <div className="card">
              <div className="card-body">
                <dl className="dl" style={{ gridTemplateColumns: '90px 1fr' }}>
                  <dt>Email</dt><dd>{editing.email}</dd>
                  <dt>WhatsApp</dt><dd>{editing.whatsapp_phone || '—'}</dd>
                  <dt>Alta</dt><dd>{relTime(editing.created_at)}</dd>
                  <dt>Último acceso</dt><dd>{editing.last_login_at ? relTime(editing.last_login_at) : 'nunca'}</dd>
                </dl>
              </div>
            </div>
            <div className="card">
              <div className="card-body col gap-4">
                <div className="form-row">
                  <label htmlFor="edit-whatsapp">WhatsApp para aprobaciones</label>
                  <input
                    id="edit-whatsapp"
                    className="input"
                    inputMode="tel"
                    value={editing.whatsapp_phone || ''}
                    onChange={(e) => setEditing((prev) => prev ? { ...prev, whatsapp_phone: e.target.value } : prev)}
                    onBlur={(e) => updateMutation.mutate({ id: editing.id, body: { whatsapp_phone: e.target.value.trim() || null } })}
                    placeholder="549351..."
                  />
                </div>
                <div className="form-row">
                  <label htmlFor="edit-role">Rol</label>
                  <select id="edit-role" className="select" value={editing.role} onChange={(e) => updateMutation.mutate({ id: editing.id, body: { role: e.target.value as PlatformRole } })}>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <label className="toggle">
                  <input type="checkbox" checked={editing.is_active} onChange={(e) => updateMutation.mutate({ id: editing.id, body: { is_active: e.target.checked } })} />
                  Activo
                </label>
              </div>
            </div>
            <AppButton variant="secondary" icon="trash2" onClick={() => { if (window.confirm(`¿Eliminar a ${userName(editing)}?`)) deleteMutation.mutate(editing.id) }}>Eliminar usuario</AppButton>
          </div>
        ) : null}
      </Drawer>
    </>
  )
}

export function ProfilePage() {
  const { data: me, isLoading } = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false })
  const name = me?.name || [me?.first_name, me?.last_name].filter(Boolean).join(' ') || me?.email || 'Equipo Bisne'
  const ini = name.split(' ').map((p) => p[0]).filter(Boolean).join('').slice(0, 2).toUpperCase() || 'BI'

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Equipo</div>
          <h1>Mi perfil</h1>
          <div className="subtitle">Tu cuenta en el panel de plataforma.</div>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 520 }}>
        <div className="card-body">
          {isLoading ? <Skeleton height={140} /> : <>
            <div className="row gap-3" style={{ alignItems: 'center', marginBottom: 18 }}>
              <Avatar initials={ini} />
              <div>
                <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 18 }}>{name}</div>
                <div className="muted" style={{ fontSize: 13 }}>{me?.email}</div>
              </div>
            </div>
            <dl className="dl" style={{ gridTemplateColumns: '120px 1fr' }}>
              <dt>Email</dt><dd>{me?.email || '—'}</dd>
              <dt>Rol</dt><dd><span className="badge badge-outline badge-sm">{me?.role || '—'}</span></dd>
            </dl>
          </>}
        </div>
      </div>
    </>
  )
}
